import { useState } from 'react';
import { Case } from '../data/demoData';
import { formatTimeAgo } from '../lib/utils';

interface CasesPanelProps {
  cases: Case[];
  onCreateCase: (c: Omit<Case, 'id' | 'createdAt'>) => void;
  onUpdateCase: (id: number, updates: Partial<Case>) => void;
}

const PRIORITY_CLASSES: Record<string, string> = {
  Critical: 'bg-red-500 text-white',
  High: 'bg-orange-500 text-white',
  Medium: 'bg-yellow-500 text-white',
  Low: 'bg-green-500 text-white',
  'Very low': 'bg-slate-400 text-white',
};

const STATUS_CLASSES: Record<string, string> = {
  New: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  Open: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
  Closed: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
};

export function CasesPanel({ cases, onCreateCase, onUpdateCase }: CasesPanelProps) {
  const [showCreate, setShowCreate] = useState(false);
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [form, setForm] = useState({ title: '', priority: 'Medium', status: 'New', assignedTo: '', description: '', incidentIds: '' });

  function handleCreate() {
    if (!form.title.trim()) return;
    onCreateCase({ title: form.title, priority: form.priority as Case['priority'], status: form.status as Case['status'], assignedTo: form.assignedTo, description: form.description, incidentIds: form.incidentIds });
    setForm({ title: '', priority: 'Medium', status: 'New', assignedTo: '', description: '', incidentIds: '' });
    setShowCreate(false);
  }

  return (
    <div className="bg-card rounded-xl shadow p-5 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-foreground">Cases</h3>
        <div className="flex gap-2">
          <button onClick={() => setShowCreate(true)} className="bg-primary text-primary-foreground px-3 py-1.5 rounded-lg text-xs font-semibold hover:opacity-90 transition-opacity">+ New Case</button>
        </div>
      </div>

      <div className="space-y-2">
        {cases.map(c => (
          <div key={c.id}
            className="flex items-center justify-between p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => setSelectedCase(c)}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${PRIORITY_CLASSES[c.priority] || 'bg-gray-400 text-white'}`}>{c.priority}</span>
                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${STATUS_CLASSES[c.status] || ''}`}>{c.status}</span>
              </div>
              <p className="text-sm font-medium text-foreground truncate">{c.title}</p>
              <p className="text-xs text-muted-foreground">{c.assignedTo || 'Unassigned'} • {formatTimeAgo(c.createdAt)}</p>
            </div>
          </div>
        ))}
        {cases.length === 0 && (
          <p className="text-center text-muted-foreground py-8 text-sm">No cases yet. Create one to track investigations.</p>
        )}
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4" onClick={() => setShowCreate(false)}>
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-blue-600">New Case</h2>
              <button onClick={() => setShowCreate(false)} className="text-3xl text-muted-foreground hover:text-foreground leading-none">&times;</button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1">Title</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full border border-border bg-background rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Brief case title…" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">Priority</label>
                  <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} className="w-full border border-border bg-background rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                    {['Critical', 'High', 'Medium', 'Low', 'Very low'].map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">Status</label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="w-full border border-border bg-background rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                    {['New', 'Open', 'Closed'].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1">Assigned To <span className="text-muted-foreground font-normal">(optional)</span></label>
                <input value={form.assignedTo} onChange={e => setForm(f => ({ ...f, assignedTo: e.target.value }))} className="w-full border border-border bg-background rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" placeholder="analyst@domain.com" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1">Description <span className="text-muted-foreground font-normal">(optional)</span></label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className="w-full border border-border bg-background rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none" placeholder="Context, scope, objectives…" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1">Linked Incidents <span className="text-muted-foreground font-normal">(comma-separated IDs)</span></label>
                <input value={form.incidentIds} onChange={e => setForm(f => ({ ...f, incidentIds: e.target.value }))} className="w-full border border-border bg-background rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" placeholder="e.g. 18450, 18455" />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowCreate(false)} className="flex-1 border border-border bg-muted/50 text-foreground py-2.5 rounded-lg font-semibold text-sm">Cancel</button>
              <button onClick={handleCreate} className="flex-1 bg-primary text-primary-foreground py-2.5 rounded-lg font-semibold text-sm hover:opacity-90">Create Case</button>
            </div>
          </div>
        </div>
      )}

      {selectedCase && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4" onClick={() => setSelectedCase(null)}>
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-lg font-bold text-foreground">{selectedCase.title}</h2>
              <button onClick={() => setSelectedCase(null)} className="text-3xl text-muted-foreground hover:text-foreground leading-none">&times;</button>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Priority</p>
                <span className={`text-xs font-semibold px-2 py-1 rounded ${PRIORITY_CLASSES[selectedCase.priority]}`}>{selectedCase.priority}</span>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Status</p>
                <span className={`text-xs font-semibold px-2 py-1 rounded ${STATUS_CLASSES[selectedCase.status]}`}>{selectedCase.status}</span>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Assigned To</p>
                <p className="text-sm text-foreground">{selectedCase.assignedTo || <em className="text-muted-foreground">Unassigned</em>}</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Created</p>
                <p className="text-sm text-foreground">{formatTimeAgo(selectedCase.createdAt)}</p>
              </div>
            </div>
            {selectedCase.description && (
              <div className="bg-muted/50 rounded-lg p-3 mb-4">
                <p className="text-xs text-muted-foreground mb-1">Description</p>
                <p className="text-sm text-foreground">{selectedCase.description}</p>
              </div>
            )}
            {selectedCase.incidentIds && (
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Linked Incidents</p>
                <p className="text-sm font-mono text-foreground">{selectedCase.incidentIds}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
